import type z from 'zod';
import { useMemo } from 'react';
import { Activity } from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';

import {
  EngagementMetrics,
  MonthlyVisitorsChart,
  TrafficSourcesChart,
  VisitorsCountryMap,
} from '@/components/templates/shared';
import {
  calculateTrend,
  formatVisitorsCountriesDateRange,
  processEngagements,
  processTrafficSources,
  processVisitorsCountries,
} from '@/components/templates/shared/WebsiteCredibility/utils';
import { ReportWrapper } from '../components';

type ReportData = NonNullable<z.infer<typeof ReportSchema>['data']>;
type TrafficAnalysisSectionProps = {
  trafficSources?: ReportData['trafficSources'] | null;
  visitorsCountries?: ReportData['visitorsCountries'] | null;
  monthlyVisits?: ReportData['monthlyVisits'] | null;
  pagesPerVisit?: number | string | null;
  timeOnSite?: number | string | null;
  bounceRate?: number | string | null;
};

export const TrafficAnalysisSection = ({
  trafficSources,
  visitorsCountries,
  monthlyVisits,
  pagesPerVisit,
  timeOnSite,
  bounceRate,
}: TrafficAnalysisSectionProps) => {
  const processedTrafficSources = useMemo(() => {
    return processTrafficSources(trafficSources);
  }, [trafficSources]);

  const processedEngagements = useMemo(() => {
    return processEngagements(timeOnSite, pagesPerVisit, bounceRate);
  }, [timeOnSite, pagesPerVisit, bounceRate]);

  const processedVisitorsCountries = useMemo(() => {
    return processVisitorsCountries(visitorsCountries);
  }, [visitorsCountries]);

  const visitorsCountriesDateRange = useMemo(() => {
    return formatVisitorsCountriesDateRange(
      visitorsCountries?.startDate,
      visitorsCountries?.endDate,
    );
  }, [visitorsCountries?.startDate, visitorsCountries?.endDate]);

  const trend = useMemo(() => {
    return calculateTrend(
      Object.entries(monthlyVisits ?? {}).map(([label, value]) => ({
        label,
        value: Number(value),
      })),
    );
  }, [monthlyVisits]);

  if (
    (!trafficSources || Object.keys(trafficSources).length === 0) &&
    (!visitorsCountries || Object.keys(visitorsCountries).length === 0) &&
    (!monthlyVisits || Object.keys(monthlyVisits).length === 0) &&
    !pagesPerVisit &&
    !timeOnSite &&
    !bounceRate
  ) {
    return (
      <ReportWrapper
        title="Traffic Analysis"
        Icon={Activity}
        tooltip="Website traffic patterns, visitor behavior, and engagement metrics analysis."
        showTooltip={false}
      >
        <div className="bg-card text-card-foreground rounded-lg border px-6 py-4 shadow-sm">
          The website does not have enough traffic data available to generate insights.
        </div>
      </ReportWrapper>
    );
  }

  return (
    <ReportWrapper
      title="Traffic Analysis"
      Icon={Activity}
      tooltip="Website traffic patterns, visitor behavior, and engagement metrics analysis."
      showTooltip={false}
    >
      <div className="space-y-6">
        <div className="flex h-auto w-full flex-col gap-4 2xl:!h-[30rem] 2xl:!flex-row">
          <MonthlyVisitorsChart monthlyVisits={monthlyVisits ?? undefined} trend={trend} />
          <div className="flex h-[15rem] w-full gap-4 2xl:h-full 2xl:w-2/5 2xl:flex-col">
            <TrafficSourcesChart trafficSources={processedTrafficSources} />
            <EngagementMetrics engagements={processedEngagements} />
          </div>
        </div>
        <VisitorsCountryMap
          visitorsCountries={processedVisitorsCountries}
          visitorsCountriesDateRange={visitorsCountriesDateRange}
        />
      </div>
    </ReportWrapper>
  );
};
